import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useInstantQrPrototypeReady } from "@/hooks/use-instant-qr-prototype-ready";
import { preserveRouteScrollThroughTransition } from "@/hooks/use-route-scroll-restoration";
import { resolveLinkingQrImageSrc } from "@/lib/account-linking";
import { pollLinkStatus, requestPairingCode } from "@/lib/api";
import {
  buildInstantQrPrototypeSearch,
  getInstantQrPrototypeState,
  INSTANT_QR_PROTOTYPE_POLL_DELAY_MS,
  shouldResetInstantQrPrototypeOnReload,
} from "@/lib/instant-qr-prototype";
import {
  claimLandingInstantLink,
  isRetryableLandingInstantLinkClaimError,
  loadLandingInstantQr,
} from "@/lib/landing-instant-link-api";

const STATUS_POLL_MS = 1_500;
const QR_LEASE_RETRY_MS = 1_000;
const CLAIM_RETRY_MAX_MS = 5_000;
const ALLOWED_STATES = new Set(["qr", "phone", "code", "linked", "configured"]);
const TERMINAL_STATUSES = new Set(["expired", "failed"]);
let designReloadResetConsumed = false;

const EMPTY_MODEL = Object.freeze({
  available: false,
  pairingCode: "",
  polling: false,
  qrImageSrc: "",
  recovering: false,
  state: "qr",
});

export function useLandingInstantLink({
  designModeEnabled = false,
  onFallback,
  productionEnabled = false,
} = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [model, setModel] = useState(EMPTY_MODEL);
  const [awakeSignal, setAwakeSignal] = useState(0);
  const [claimRequested, setClaimRequested] = useState(false);
  const [designPolling, setDesignPolling] = useState(false);
  const designPollingTimeoutRef = useRef(null);
  const designReloadResetRef = useRef(null);
  const fallbackRef = useRef(onFallback);
  const linkSessionIdRef = useRef("");
  const manualClaimRef = useRef(false);
  const mountedRef = useRef(false);
  const qrRefreshPhaseRef = useRef("");
  const qrRefreshPromiseRef = useRef(null);
  const stateRef = useRef("qr");
  const designQrReady = useInstantQrPrototypeReady(designModeEnabled);
  if (designReloadResetRef.current === null) {
    const navigationType = window.performance?.getEntriesByType?.("navigation")?.[0]?.type || "navigate";
    designReloadResetRef.current = !designReloadResetConsumed && designModeEnabled
      && shouldResetInstantQrPrototypeOnReload(location.search, navigationType);
    if (designReloadResetRef.current) designReloadResetConsumed = true;
  }
  const designState = designModeEnabled
    ? designReloadResetRef.current ? "qr" : getInstantQrPrototypeState(location.search)
    : "qr";
  const designStateRef = useRef(designState);

  fallbackRef.current = onFallback;
  stateRef.current = model.state;
  designStateRef.current = designState;

  useEffect(() => {
    if (!designModeEnabled || !designReloadResetRef.current) return;
    designReloadResetRef.current = false;
    designStateRef.current = "qr";
    navigate({
      pathname: location.pathname,
      search: buildInstantQrPrototypeSearch(location.search, "qr"),
    }, { replace: true, state: { ...(location.state || {}), preserveScroll: true } });
  }, [designModeEnabled, location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (designState === "qr") return undefined;
    window.clearTimeout(designPollingTimeoutRef.current);
    designPollingTimeoutRef.current = null;
    setDesignPolling(false);
    return undefined;
  }, [designState]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      window.clearTimeout(designPollingTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (designModeEnabled || !productionEnabled) return undefined;
    setClaimRequested(true);
    return undefined;
  }, [designModeEnabled, productionEnabled]);

  useEffect(() => {
    if (!productionEnabled || designModeEnabled || !claimRequested) {
      linkSessionIdRef.current = "";
      qrRefreshPhaseRef.current = "";
      qrRefreshPromiseRef.current = null;
      stateRef.current = "qr";
      setModel(EMPTY_MODEL);
      if (!productionEnabled || designModeEnabled) {
        manualClaimRef.current = false;
        setClaimRequested(false);
      }
      return undefined;
    }

    let cancelled = false;
    let prepareTimer = null;
    let rotateTimer = null;
    let expiryTimer = null;
    let claimRetryTimer = null;
    let claimRetryAttempt = 0;
    let bootstrapPromise = null;
    let statusPollTimer = null;
    let statusPollInFlight = false;
    let activeLease = null;

    const clearLeaseTimers = () => {
      window.clearTimeout(prepareTimer);
      window.clearTimeout(rotateTimer);
      window.clearTimeout(expiryTimer);
      prepareTimer = null;
      rotateTimer = null;
      expiryTimer = null;
    };

    const markUnavailable = () => {
      if (cancelled) return;
      linkSessionIdRef.current = "";
      activeLease = null;
      setModel((current) => ({
        ...current,
        available: false,
        polling: false,
        qrImageSrc: "",
        recovering: true,
      }));
      clearLeaseTimers();
    };

    const hideExpiredQr = () => {
      if (cancelled) return;
      setModel((current) => ({
        ...current,
        available: false,
        qrImageSrc: "",
        recovering: true,
      }));
      clearLeaseTimers();
    };

    let refreshQr;
    let recoverExpiredQr;
    const scheduleLease = (payload) => {
      const generation = Number(payload?.generation);
      const serverNowMs = Date.parse(payload?.serverNow || "");
      const prepareAtMs = Date.parse(payload?.prepareAt || "");
      const swapAtMs = Date.parse(payload?.swapAt || "");
      const expiresAtMs = Date.parse(payload?.expiresAt || "");
      if (
        !Number.isInteger(generation)
        || ![serverNowMs, prepareAtMs, swapAtMs, expiresAtMs].every(Number.isFinite)
        || !(serverNowMs < expiresAtMs && prepareAtMs < swapAtMs && swapAtMs < expiresAtMs)
      ) {
        throw new Error("QR lease timing is invalid.");
      }
      clearLeaseTimers();
      activeLease = {
        generation,
        prepareDeadline: Date.now() + Math.max(0, prepareAtMs - serverNowMs),
        swapDeadline: Date.now() + Math.max(0, swapAtMs - serverNowMs),
        expiryDeadline: Date.now() + Math.max(0, expiresAtMs - serverNowMs),
      };
      const prepareSuccessor = async () => {
        if (cancelled || stateRef.current !== "qr" || activeLease?.generation !== generation) return;
        try {
          await refreshQr({ phase: "prepare" });
        } catch {
          if (!cancelled && Date.now() < activeLease.expiryDeadline) {
            prepareTimer = window.setTimeout(prepareSuccessor, QR_LEASE_RETRY_MS);
          }
        }
      };
      if (!payload?.successorReady) {
        prepareTimer = window.setTimeout(
          prepareSuccessor,
          Math.max(0, activeLease.prepareDeadline - Date.now()),
        );
      }
      rotateTimer = window.setTimeout(
        recoverExpiredQr,
        Math.max(0, activeLease.swapDeadline - Date.now()),
      );
      expiryTimer = window.setTimeout(() => {
        if (activeLease?.generation !== generation) return;
        hideExpiredQr();
        void recoverExpiredQr();
      }, Math.max(0, activeLease.expiryDeadline - Date.now()));
    };

    refreshQr = async ({ phase = "current" } = {}) => {
      const linkSessionId = linkSessionIdRef.current;
      if (!linkSessionId || stateRef.current !== "qr") return null;
      if (qrRefreshPromiseRef.current) {
        const inFlightPhase = qrRefreshPhaseRef.current;
        await qrRefreshPromiseRef.current;
        if (phase === "rotate" && inFlightPhase !== "rotate") {
          return refreshQr({ phase });
        }
        return null;
      }
      let refreshPromise = null;
      try {
        const expectedGeneration = phase === "current"
          ? null
          : activeLease?.generation;
        refreshPromise = loadLandingInstantQr(linkSessionId, {
          expectedGeneration,
          phase,
        });
        qrRefreshPhaseRef.current = phase;
        qrRefreshPromiseRef.current = refreshPromise;
        const payload = await refreshPromise;
        const qrImageSrc = resolveLinkingQrImageSrc(payload);
        if (!qrImageSrc) throw new Error("QR payload is not ready.");
        if (!cancelled) {
          scheduleLease(payload);
          setModel((current) => ({
            ...current,
            available: true,
            polling: current.state !== "linked",
            qrImageSrc,
            recovering: false,
          }));
        }
        return payload;
      } finally {
        if (qrRefreshPromiseRef.current === refreshPromise) {
          qrRefreshPhaseRef.current = "";
          qrRefreshPromiseRef.current = null;
        }
      }
    };

    recoverExpiredQr = async () => {
      if (cancelled || !linkSessionIdRef.current || stateRef.current !== "qr") return;
      const attemptedLease = activeLease;
      try {
        await refreshQr({ phase: "rotate" });
      } catch {
        if (cancelled || !linkSessionIdRef.current || stateRef.current !== "qr") return;
        if (attemptedLease && Date.now() >= attemptedLease.expiryDeadline) {
          hideExpiredQr();
          linkSessionIdRef.current = "";
          scheduleClaimRetry();
          return;
        }
        rotateTimer = window.setTimeout(recoverExpiredQr, QR_LEASE_RETRY_MS);
      }
    };

    const pollStatus = async () => {
      const linkSessionId = linkSessionIdRef.current;
      if (cancelled || !linkSessionId || statusPollInFlight) return;
      statusPollInFlight = true;
      try {
        const payload = await pollLinkStatus(linkSessionId);
        const status = String(payload?.status || "").trim().toLowerCase();
        if (status === "linked") {
          stateRef.current = "linked";
          setModel((current) => ({ ...current, polling: false, state: "linked" }));
          return;
        }
        if (TERMINAL_STATUSES.has(status)) {
          try {
            await refreshQr({ phase: "rotate" });
          } catch {
            markUnavailable();
            scheduleClaimRetry();
            return;
          }
        }
      } catch {
        // A transient status read must not discard a still-valid QR.
      } finally {
        statusPollInFlight = false;
      }
      if (!cancelled) statusPollTimer = window.setTimeout(pollStatus, STATUS_POLL_MS);
    };

    let bootstrap;
    const scheduleClaimRetry = (delayOverride = null) => {
      if (cancelled || claimRetryTimer !== null) return;
      const delay = delayOverride ?? Math.min(
        CLAIM_RETRY_MAX_MS,
        QR_LEASE_RETRY_MS * (2 ** Math.min(claimRetryAttempt, 3)),
      );
      claimRetryAttempt += 1;
      claimRetryTimer = window.setTimeout(() => {
        claimRetryTimer = null;
        void bootstrap();
      }, delay);
    };

    const runBootstrap = async () => {
      try {
        const claim = await claimLandingInstantLink();
        const linkSessionId = String(claim?.linkSessionId || "").trim();
        if (!linkSessionId || cancelled) return;
        linkSessionIdRef.current = linkSessionId;
        await refreshQr();
        if (cancelled) return;
        claimRetryAttempt = 0;
        if (manualClaimRef.current) setAwakeSignal((current) => current + 1);
        manualClaimRef.current = false;
        void pollStatus();
      } catch (error) {
        if (cancelled) return;
        manualClaimRef.current = false;
        if (!isRetryableLandingInstantLinkClaimError(error)) {
          linkSessionIdRef.current = "";
          setModel(EMPTY_MODEL);
          setClaimRequested(false);
          fallbackRef.current?.(error);
          return;
        }
        markUnavailable();
        scheduleClaimRetry();
      }
    };

    bootstrap = () => {
      if (bootstrapPromise) return bootstrapPromise;
      const inFlight = runBootstrap().finally(() => {
        if (bootstrapPromise === inFlight) bootstrapPromise = null;
      });
      bootstrapPromise = inFlight;
      return inFlight;
    };

    const refreshAfterResume = () => {
      if (cancelled || stateRef.current !== "qr") return;
      if (document.visibilityState === "hidden") return;
      if (!linkSessionIdRef.current) {
        if (!bootstrapPromise) scheduleClaimRetry(0);
        return;
      }
      if (activeLease && Date.now() >= activeLease.expiryDeadline) {
        hideExpiredQr();
        void recoverExpiredQr();
        return;
      }
      const phase = activeLease && Date.now() >= activeLease.swapDeadline
        ? "rotate"
        : "current";
      void refreshQr({ phase }).catch(() => {
        if (activeLease && Date.now() >= activeLease.expiryDeadline) markUnavailable();
      });
    };

    void bootstrap();
    window.addEventListener("focus", refreshAfterResume);
    window.addEventListener("online", refreshAfterResume);
    window.addEventListener("pageshow", refreshAfterResume);
    document.addEventListener("visibilitychange", refreshAfterResume);
    return () => {
      cancelled = true;
      clearLeaseTimers();
      window.clearTimeout(statusPollTimer);
      window.clearTimeout(claimRetryTimer);
      window.removeEventListener("focus", refreshAfterResume);
      window.removeEventListener("online", refreshAfterResume);
      window.removeEventListener("pageshow", refreshAfterResume);
      document.removeEventListener("visibilitychange", refreshAfterResume);
    };
  }, [claimRequested, designModeEnabled, productionEnabled]);

  const setProductionState = useCallback((nextState, {
    preservePosition,
    preserveTransitionScroll = true,
  } = {}) => {
    const normalizedState = String(nextState || "").trim().toLowerCase();
    if (!ALLOWED_STATES.has(normalizedState)) return;
    stateRef.current = normalizedState;
    if (preserveTransitionScroll) {
      preserveRouteScrollThroughTransition({ settleMs: 320, position: preservePosition });
    }
    setModel((current) => ({ ...current, state: normalizedState }));
  }, []);

  const setExperienceState = useCallback((nextState, {
    preservePosition,
    preserveTransitionScroll = true,
  } = {}) => {
    if (!designModeEnabled) {
      setProductionState(nextState, { preservePosition, preserveTransitionScroll });
      return;
    }
    const normalizedState = String(nextState || "").trim().toLowerCase();
    if (!ALLOWED_STATES.has(normalizedState) || designStateRef.current === normalizedState) return;
    designStateRef.current = normalizedState;
    if (preserveTransitionScroll) {
      preserveRouteScrollThroughTransition({ settleMs: 320, position: preservePosition });
    }
    navigate({
      pathname: location.pathname,
      search: buildInstantQrPrototypeSearch(location.search, normalizedState),
    }, { state: { ...(location.state || {}), preserveScroll: true } });
  }, [designModeEnabled, location.pathname, location.search, location.state, navigate, setProductionState]);

  const requestCode = useCallback(async (phoneNumber) => {
    const linkSessionId = linkSessionIdRef.current;
    if (!linkSessionId) {
      fallbackRef.current?.();
      return false;
    }
    stateRef.current = "phone";
    setModel((current) => ({ ...current, polling: false, state: "phone" }));
    try {
      try {
        await qrRefreshPromiseRef.current;
      } catch {
        // The pairing-code request is authoritative after any in-flight QR read settles.
      }
      if (!mountedRef.current) return false;
      const payload = await requestPairingCode(linkSessionId, phoneNumber);
      if (!mountedRef.current) return false;
      const pairingCode = String(payload?.code || payload?.pairingCode || "").trim();
      if (!pairingCode) throw new Error("Pairing code is not ready.");
      setModel((current) => ({ ...current, pairingCode }));
      return true;
    } catch {
      if (mountedRef.current) fallbackRef.current?.();
      return false;
    }
  }, []);

  const openWhatsApp = useCallback(() => {
    window.location.assign("whatsapp://send");
  }, []);

  const activate = useCallback(() => {
    if (!designModeEnabled) {
      setModel((current) => ({ ...current, polling: current.available }));
      return;
    }
    if (
      designStateRef.current !== "qr"
      || designPollingTimeoutRef.current !== null
    ) return;
    setDesignPolling(true);
    designPollingTimeoutRef.current = window.setTimeout(() => {
      designPollingTimeoutRef.current = null;
      setDesignPolling(false);
      setExperienceState("linked");
    }, INSTANT_QR_PROTOTYPE_POLL_DELAY_MS);
  }, [designModeEnabled, setExperienceState]);

  const awaken = useCallback(() => {
    setAwakeSignal((current) => current + 1);
    if (!designModeEnabled && productionEnabled) {
      manualClaimRef.current = true;
      setClaimRequested(true);
    }
  }, [designModeEnabled, productionEnabled]);

  if (designModeEnabled) {
    return {
      awakeSignal,
      enabled: true,
      onActivate: activate,
      onAwaken: awaken,
      onOpenWhatsApp: undefined,
      onRequestCode: undefined,
      onStateChange: setExperienceState,
      pairingCode: "",
      polling: designPolling,
      qrImageSrc: "/landing/instant-qr-demo.png",
      qrReady: designQrReady,
      state: designState,
    };
  }

  return {
    ...model,
    armed: productionEnabled,
    awakeSignal,
    enabled: productionEnabled
      && (model.recovering || (model.available && Boolean(model.qrImageSrc))),
    onActivate: activate,
    onAwaken: awaken,
    onOpenWhatsApp: openWhatsApp,
    onRequestCode: requestCode,
    onStateChange: setExperienceState,
    qrReady: model.available && Boolean(model.qrImageSrc),
  };
}
